import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Check, Copy, Shield } from 'lucide-react';
import { useCountUpOnVisible } from '../lib/useCountUpOnVisible';

const SLIDES = [
  {
    key: 'roadmap',
    caption: 'A prioritised action plan tailored to your business',
    Mock: RoadmapMock,
  },
  {
    key: 'policy',
    caption: 'Professional policy documents ready to send',
    Mock: PolicyMock,
  },
  {
    key: 'score',
    caption: 'Track your security posture over time',
    Mock: ScoreMock,
  },
  {
    key: 'trust',
    caption: 'A public Trust Page to share with any prospect',
    Mock: TrustMock,
  },
];

const ProductShowcase = () => {
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);

  // Defer all carousel work until the section is within 300px of the viewport.
  useEffect(() => {
    if (inView) return;
    const node = sectionRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: '300px 0px' }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [inView]);

  return (
    <section
      ref={sectionRef}
      data-testid="landing-product-showcase"
      className="bg-[#f5f3ef] py-14 sm:py-20"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-14">
          <h3
            className="text-3xl sm:text-4xl lg:text-5xl font-normal leading-tight mb-3"
            style={{ fontFamily: 'Instrument Serif, serif' }}
          >
            See exactly what you get
          </h3>
          <p className="text-base sm:text-lg text-[#1a1a1a]/70">
            Your full compliance package, ready in under an hour.
          </p>
        </div>

        {inView ? (
          <Carousel />
        ) : (
          <div
            data-testid="showcase-skeleton"
            aria-hidden="true"
            className="rounded-2xl border border-[#e0ddd8] bg-white min-h-[420px] sm:min-h-[520px]"
          />
        )}
      </div>
    </section>
  );
};

const Carousel = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  // Slides that have ever been mounted. Once a slide mounts it stays mounted
  // so internal state (e.g., ScoreMock checklist) is preserved.
  const [mounted, setMounted] = useState(() => new Set([0, 1 % SLIDES.length]));
  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);

  const next = useCallback(() => setActive((i) => (i + 1) % SLIDES.length), []);
  const prev = useCallback(
    () => setActive((i) => (i - 1 + SLIDES.length) % SLIDES.length),
    []
  );

  // Whenever `active` changes, ensure the active slide and the next slide
  // (preload) are mounted. Already-mounted slides remain mounted.
  useEffect(() => {
    setMounted((prevSet) => {
      const nextSet = new Set(prevSet);
      const before = nextSet.size;
      nextSet.add(active);
      nextSet.add((active + 1) % SLIDES.length);
      return nextSet.size === before ? prevSet : nextSet;
    });
  }, [active]);

  // Auto-advance every 4s, paused on hover or touch
  useEffect(() => {
    if (paused) return;
    const t = setInterval(next, 4000);
    return () => clearInterval(t);
  }, [paused, next]);

  // Touch handlers for mobile swipe
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
    setPaused(true);
  };
  const onTouchMove = (e) => {
    if (touchStartX.current == null) return;
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  };
  const onTouchEnd = () => {
    const d = touchDeltaX.current;
    if (Math.abs(d) > 40) {
      if (d < 0) next();
      else prev();
    }
    touchStartX.current = null;
    touchDeltaX.current = 0;
    setPaused(false);
  };

  return (
    <div
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Slide viewport */}
      <div
        className="overflow-hidden rounded-2xl border border-[#e0ddd8] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(10,92,68,0.08)]"
        data-testid="showcase-viewport"
      >
        <div
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${active * 100}%)` }}
        >
          {SLIDES.map(({ key, Mock }, idx) => (
            <div
              key={key}
              className="w-full flex-shrink-0 px-4 sm:px-10 py-8 sm:py-12"
              data-testid={`showcase-slide-${key}`}
            >
              <div className="max-w-3xl mx-auto">
                {mounted.has(idx) ? <Mock /> : null}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Caption */}
      <p
        data-testid="showcase-caption"
        className="text-center text-sm sm:text-base text-[#1a1a1a]/70 mt-5 sm:mt-6 px-4 min-h-[1.5em]"
      >
        {SLIDES[active].caption}
      </p>

      {/* Arrows — hidden on smallest mobile, visible from sm up */}
      <button
        type="button"
        onClick={prev}
        data-testid="showcase-prev"
        aria-label="Previous slide"
        className="hidden sm:flex absolute left-2 sm:-left-4 top-1/2 -translate-y-1/2 items-center justify-center w-10 h-10 rounded-full bg-white border border-[#e0ddd8] hover:border-[#0a5c44] hover:text-[#0a5c44] text-[#1a1a1a]/60 transition-colors shadow-sm"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={next}
        data-testid="showcase-next"
        aria-label="Next slide"
        className="hidden sm:flex absolute right-2 sm:-right-4 top-1/2 -translate-y-1/2 items-center justify-center w-10 h-10 rounded-full bg-white border border-[#e0ddd8] hover:border-[#0a5c44] hover:text-[#0a5c44] text-[#1a1a1a]/60 transition-colors shadow-sm"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* Dots */}
      <div className="flex items-center justify-center gap-2 mt-6">
        {SLIDES.map((s, i) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setActive(i)}
            aria-label={`Go to slide ${i + 1}`}
            data-testid={`showcase-dot-${i}`}
            className={`transition-all rounded-full ${
              i === active
                ? 'w-8 h-2 bg-[#0a5c44]'
                : 'w-2 h-2 bg-[#1a1a1a]/20 hover:bg-[#1a1a1a]/40'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

/* -------------------- Slide mockups -------------------- */

const PriorityBadge = ({ level }) => {
  const styles = {
    High: 'bg-red-50 text-red-700 border-red-200',
    Medium: 'bg-yellow-50 text-yellow-800 border-yellow-200',
    Low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  };
  return (
    <span
      className={`inline-flex items-center text-[11px] font-semibold tracking-wide uppercase border px-2.5 py-1 rounded-full ${styles[level]}`}
    >
      {level}
    </span>
  );
};

function RoadmapMock() {
  const items = [
    {
      title: 'Formalize Data Processing',
      desc: 'Document how customer data flows through your stack and where it lives.',
      level: 'High',
    },
    {
      title: 'Implement Access Controls',
      desc: 'Roll out SSO and least-privilege roles across production systems.',
      level: 'Medium',
    },
    {
      title: 'Employee Security Training',
      desc: 'Quarterly phishing and data-handling refreshers for the whole team.',
      level: 'Low',
    },
  ];
  return (
    <div className="space-y-3 sm:space-y-4">
      <h4
        className="text-xl sm:text-2xl font-normal mb-1"
        style={{ fontFamily: 'Instrument Serif, serif' }}
      >
        Compliance roadmap
      </h4>
      {items.map((it) => (
        <div
          key={it.title}
          tabIndex={0}
          className="group flex items-start gap-3 sm:gap-4 bg-white border border-[#e0ddd8] rounded-xl p-4 sm:p-5 cursor-default transition-all duration-300 ease-out hover:-translate-y-0.5 hover:scale-[1.015] hover:border-[#0a5c44]/40 hover:shadow-[0_8px_24px_-12px_rgba(10,92,68,0.25)] focus-visible:outline-none focus-visible:border-[#0a5c44]/40 focus-visible:shadow-[0_8px_24px_-12px_rgba(10,92,68,0.25)]"
        >
          <div className="flex-1 min-w-0">
            <p className="text-sm sm:text-base font-semibold text-[#1a1a1a] mb-1.5 transition-colors group-hover:text-[#0a5c44]">
              {it.title}
            </p>
            <p className="text-xs sm:text-sm text-[#1a1a1a]/65 leading-relaxed">
              {it.desc}
            </p>
          </div>
          <div className="shrink-0 pt-0.5">
            <PriorityBadge level={it.level} />
          </div>
        </div>
      ))}
    </div>
  );
}

function PolicyMock() {
  return (
    <div className="bg-white border border-[#e0ddd8] rounded-xl p-5 sm:p-7 relative">
      <div className="flex items-start justify-between mb-5 sm:mb-6">
        <div>
          <p className="text-[11px] font-semibold tracking-wider uppercase text-[#0a5c44] mb-1.5">
            Policy document
          </p>
          <h4
            className="text-xl sm:text-2xl font-normal leading-tight"
            style={{ fontFamily: 'Instrument Serif, serif' }}
          >
            Data Handling Policy
          </h4>
        </div>
        <button
          type="button"
          aria-label="Copy policy"
          className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-[#1a1a1a]/60 hover:text-[#0a5c44] border border-[#e0ddd8] hover:border-[#0a5c44] rounded-lg px-2.5 py-1.5 transition-colors"
        >
          <Copy className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Copy</span>
        </button>
      </div>

      <div className="space-y-4 sm:space-y-5">
        <PolicySection
          label="Policy statement"
          body="This policy defines how customer data is collected, processed, stored, and protected across all systems. Applies to every employee, contractor, and integrated service."
        />
        <PolicySection
          label="Data collection"
          body="Only the minimum data required to deliver the product is collected. All collection points are documented and reviewed quarterly by the security lead."
        />
        <PolicySection
          label="Data retention"
          body="Customer records are retained for the active subscription period plus 30 days. Backups are encrypted and rotated on a 90-day cycle."
        />
      </div>
    </div>
  );
}

function PolicySection({ label, body }) {
  return (
    <div>
      <p className="text-[11px] font-semibold tracking-wider uppercase text-[#1a1a1a]/55 mb-1.5">
        {label}
      </p>
      <p className="text-xs sm:text-sm text-[#1a1a1a]/75 leading-relaxed">
        {body}
      </p>
    </div>
  );
}

function ScoreMock() {
  const initial = [
    { id: 1, label: 'Enable MFA across production accounts', done: true },
    { id: 2, label: 'Publish updated incident response plan', done: true },
    { id: 3, label: 'Run quarterly access review', done: false },
  ];
  const [tasks, setTasks] = useState(initial);
  const total = tasks.length;
  const completed = tasks.filter((t) => t.done).length;
  // Base 35, +15 per completed task → 35 / 50 / 65 / 80 over 0–3 done
  const score = 35 + completed * 15;

  // Animate from 0 to `score` when the score panel scrolls into view.
  // After the count-up finishes, switch to the live `score` so user clicks
  // on the checklist update the number immediately.
  const { ref: visibilityRef, display: animatedScore, complete: countComplete } =
    useCountUpOnVisible(score, 1500);
  const shownScore = countComplete ? score : animatedScore;

  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (shownScore / 100) * circumference;

  const toggle = (id) =>
    setTasks((prev) =>
      prev.map((t) => (t.id === id ? { ...t, done: !t.done } : t))
    );

  return (
    <div ref={visibilityRef} className="flex flex-col items-center text-center">
      <div className="relative w-36 h-36 sm:w-44 sm:h-44 mb-5">
        <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke="#e0ddd8"
            strokeWidth="10"
          />
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke="#0a5c44"
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{ transition: countComplete ? 'stroke-dashoffset 600ms cubic-bezier(0.22, 1, 0.36, 1)' : 'none' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span
            key={countComplete ? score : 'animating'}
            className={`text-4xl sm:text-5xl font-normal text-[#0a5c44] leading-none ${countComplete ? 'animate-[scoreTick_400ms_ease-out]' : ''}`}
            style={{ fontFamily: 'Instrument Serif, serif' }}
          >
            {shownScore}
          </span>
          <span className="text-[11px] font-semibold tracking-wider uppercase text-[#1a1a1a]/55 mt-1">
            of 100
          </span>
        </div>
      </div>

      <h4
        className="text-xl sm:text-2xl font-normal mb-1.5"
        style={{ fontFamily: 'Instrument Serif, serif' }}
      >
        Your Compliance Score
      </h4>
      <p className="text-xs sm:text-sm text-[#1a1a1a]/65 max-w-md mb-5 sm:mb-6">
        Complete your monthly checklist to improve your score.{' '}
        <span className="text-[#1a1a1a]/45">
          ({completed}/{total} done — try clicking)
        </span>
      </p>

      <ul className="w-full max-w-md space-y-2 text-left">
        {tasks.map((t) => (
          <li key={t.id}>
            <button
              type="button"
              onClick={() => toggle(t.id)}
              aria-pressed={t.done}
              className="w-full flex items-center gap-3 bg-white border border-[#e0ddd8] rounded-lg px-3.5 py-2.5 text-left transition-all duration-200 hover:border-[#0a5c44]/40 hover:shadow-[0_4px_16px_-8px_rgba(10,92,68,0.2)] focus-visible:outline-none focus-visible:border-[#0a5c44]/60"
            >
              <span
                className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-all duration-200 ${
                  t.done
                    ? 'bg-[#0a5c44] border-[#0a5c44] scale-100'
                    : 'bg-white border-[#1a1a1a]/25 scale-100'
                }`}
              >
                <Check
                  className={`w-3 h-3 text-white transition-all duration-200 ${
                    t.done ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
                  }`}
                  strokeWidth={3}
                />
              </span>
              <span
                className={`text-xs sm:text-sm transition-all duration-200 ${
                  t.done
                    ? 'text-[#1a1a1a]/45 line-through'
                    : 'text-[#1a1a1a]/85'
                }`}
              >
                {t.label}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TrustMock() {
  const policies = [
    'Data Handling Policy',
    'Access Control Policy',
    'Incident Response Policy',
  ];
  return (
    <div>
      <div className="flex items-center gap-2 mb-4 sm:mb-5">
        <Shield className="w-4 h-4 text-[#0a5c44]" />
        <span className="text-[11px] font-semibold tracking-wider uppercase text-[#1a1a1a]/55">
          Public trust page
        </span>
      </div>
      <h4
        className="text-2xl sm:text-3xl font-normal mb-4 sm:mb-5 leading-tight"
        style={{ fontFamily: 'Instrument Serif, serif' }}
      >
        Security &amp; Compliance
      </h4>

      <div className="bg-[#f5f3ef] border border-[#e0ddd8] rounded-xl p-5 sm:p-6 mb-5 sm:mb-6">
        <p className="text-[11px] font-semibold tracking-wider uppercase text-[#0a5c44] mb-2">
          Our commitment
        </p>
        <p className="text-xs sm:text-sm text-[#1a1a1a]/75 leading-relaxed">
          We treat customer data with enterprise-grade controls: encryption in
          transit and at rest, least-privilege access, and a documented incident
          response plan reviewed every quarter.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        {policies.map((p) => (
          <div
            key={p}
            className="bg-white border border-[#e0ddd8] rounded-xl p-4 sm:p-5"
          >
            <Check className="w-5 h-5 text-[#0a5c44] mb-2.5" />
            <p className="text-xs sm:text-sm font-semibold text-[#1a1a1a] leading-snug">
              {p}
            </p>
            <p className="text-[11px] text-[#1a1a1a]/55 mt-1.5">
              In place &amp; reviewed
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ProductShowcase;
